"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useStudio } from "@/contexts/studio-context";
import { Note } from "@/lib/music/note";
import type { ScheduledNote } from "@/components/learn-piece/practice";

/**
 * Sounding notes **as the application**, on the same piano the player uses.
 *
 * Every press and release carries `origin: "app"`, which is what keeps the
 * learning journey's watcher from taking them for answers: before the origin
 * existed, anything the app played walked the exercise forward on its own.
 * The release carries it too — `Piano.release` refuses to lift a key it did
 * not press, so a note the app sounds while the player happens to hold it
 * cannot lift their finger.
 *
 * Out-of-range notes are dropped rather than thrown: a phrase can reach past
 * what the on-screen piano covers, and a fragment that refuses to play at all
 * because of one note is worse than one that plays the rest.
 */

export function useAppPiano(): {
  readonly playing: boolean;
  readonly play: (notes: readonly ScheduledNote[], onDone?: () => void) => void;
  readonly stop: () => void;
} {
  const { studio } = useStudio();
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const soundingRef = useRef<Set<number>>(new Set());
  const [playing, setPlaying] = useState(false);

  const stop = useCallback(() => {
    for (const timer of timersRef.current) {
      clearTimeout(timer);
    }

    timersRef.current = [];

    const piano = studio?.getPiano();

    if (piano) {
      for (const midi of soundingRef.current) {
        piano.release(Note.fromMidi(midi), "app");
      }
    }

    soundingRef.current.clear();
    setPlaying(false);
  }, [studio]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const play = useCallback(
    (notes: readonly ScheduledNote[], onDone?: () => void) => {
      stop();

      const piano = studio?.getPiano();

      if (!piano || notes.length === 0) {
        onDone?.();

        return;
      }

      setPlaying(true);
      let last = 0;

      for (const n of notes) {
        const note = Note.fromMidi(n.midi);

        if (!piano.contains(note)) {
          continue;
        }

        last = Math.max(last, n.atMs + n.durMs);

        timersRef.current.push(
          setTimeout(() => {
            piano.press(note, APP_VELOCITY, "app");
            soundingRef.current.add(n.midi);
          }, n.atMs),
        );

        timersRef.current.push(
          setTimeout(() => {
            piano.release(note, "app");
            soundingRef.current.delete(n.midi);
          }, n.atMs + n.durMs),
        );
      }

      timersRef.current.push(
        setTimeout(() => {
          setPlaying(false);
          onDone?.();
        }, last + 20),
      );
    },
    [studio, stop],
  );

  return { playing, play, stop };
}

/** Softer than a played note: it is a model, not a performance. */
const APP_VELOCITY = 0.42;
